// Geometry for the crafting panel. Like the chest/trade screen, the crafting screen shows the
// player's backpack underneath its own UI -- the materials every recipe draws from. Both halves live
// in ONE docked panel that has to stay inside the 442px-tall screen, so neither half can claim a
// fixed size: the backpack picks the largest slot size whose rows still fit in what is left, and the
// recipe list takes the rest as a window it SCROLLS inside.
//
// Pure math, no pixi -- see __tests__/crafting_layout.test.ts.

// Slot sizes the backpack grid will try, largest first. Each one implies its own column count,
// fitted to the panel's inner width (32 -> 8 cols, 28 -> 9, 24 -> 10 at a 4px gap in 284px).
export const BACKPACK_SLOT_SIZES = [32, 28, 24]
export const GRID_GAP = 4
// Chests/players always show at least two rows on the trade screen; match that so a light backpack
// still reads as a grid rather than a lone strip of icons.
const MIN_ROWS = 2
// Rows the recipe list is asked to give up room for. A fuller backpack than this shrinks its own
// slots rather than squeezing the recipes further.
const MAX_RESERVED_ROWS = 3

export const RECIPE_GAP = 4
export const RECIPE_CARD_MAX_H = 52
// The comfortable floor: below this a recipe card is tight for its two lines (name + the ingredient
// line under it).
export const RECIPE_CARD_MIN_H = 36
// Height of the "No recipes available yet." placeholder that stands in for the rows.
const RECIPE_EMPTY_H = 24

// Gap between the recipe list and the backpack's section label, and from that label to its slots.
// Both are kept tight on purpose: a handful of pixels here is the difference between the pack fitting
// a third row of slots and having to report the tail of itself as "+N more".
const SECTION_GAP = 6
const LABEL_H = 18

export interface BackpackGrid {
    slot: number
    cols: number
    rows: number
    gap: number
    height: number
    // Items with no cell in the grid. Only ever non-zero in the corner where a full recipe page has
    // already eaten the panel; the screen says "+N more" rather than swallowing them silently.
    hidden: number
}

export interface CraftingLayout {
    // Height of each recipe card (0 when there are no recipes to draw).
    cardH: number
    // Height of the window the recipe list is drawn through. The list SCROLLS inside this rather than
    // being paged: a page turn is what hid the campfire, and a group that does not fit is now a short
    // scroll rather than a second page.
    viewH: number
    // Height of the whole list, scrolled or not.
    contentH: number
    // How far the list can be scrolled. Zero when it all fits, which is most groups.
    maxScroll: number
    // First y below the recipe window.
    recipesBottom: number
    labelY: number
    gridY: number
    grid: BackpackGrid
    // Bottom of the laid-out content. Past `bottomY` only in the extreme case where even the
    // smallest backpack grid overruns what the recipes left behind.
    bottom: number
}

const rowsHeight = (slot: number, rows: number): number => rows * (slot + GRID_GAP) - GRID_GAP

// What one slot size can make of `maxH`: every row it needs when they fit (padded out to MIN_ROWS if
// there is room to spare), otherwise every row it can draw.
const gridFor = (slot: number, itemCount: number, innerW: number, maxH: number): BackpackGrid => {
    const cols = Math.max(1, Math.floor((innerW + GRID_GAP) / (slot + GRID_GAP)))
    const needed = Math.max(1, Math.ceil(itemCount / cols))
    const affordable = Math.floor((maxH + GRID_GAP) / (slot + GRID_GAP))
    const rows = needed <= affordable ? Math.min(affordable, Math.max(needed, MIN_ROWS)) : Math.max(1, affordable)
    return {
        slot,
        cols,
        rows,
        gap: GRID_GAP,
        height: rowsHeight(slot, rows),
        hidden: Math.max(0, itemCount - rows * cols),
    }
}

// Preference order: the roomiest slots that still give every item a cell; failing that (a full recipe
// page has taken the panel), whichever size shows the most, so the panel stays on screen and says
// "+N more" rather than running off the bottom edge.
export const backpackGrid = (itemCount: number, innerW: number, maxH: number): BackpackGrid => {
    const candidates = BACKPACK_SLOT_SIZES.map(slot => gridFor(slot, itemCount, innerW, maxH))
    const complete = candidates.find(c => c.height <= maxH && c.hidden === 0)
    if (complete) {
        return complete
    }

    const fitting = candidates.filter(c => c.height <= maxH)
    // Ties keep the earlier (roomier) candidate.
    return (fitting.length > 0 ? fitting : candidates).reduce((best, c) => (c.hidden < best.hidden ? c : best))
}

/**
 * How the panel shares itself out between the recipe list and the pack underneath.
 *
 * The pack reserves what it wants (capped, so a full one cannot starve the list) and the recipe list
 * takes the rest as a WINDOW. A group taller than that window scrolls inside it.
 *
 * Scrolling rather than paging is the point. The list used to be paged five at a time, which put the
 * campfire -- sixth of seven structures -- on page two of the one screen a new player has to use, and
 * nothing on page one said there was a page two. A scroll is continuous and self-advertising: the bar
 * beside the list says there is more, and no recipe is ever a click away behind a number.
 *
 * Cards still get taller when a group is short, which is why cardH is derived rather than fixed; once
 * a group is long enough to scroll they settle at RECIPE_CARD_MIN_H and the list simply gets longer.
 */
export const craftingLayout = ({
    topY,
    bottomY,
    innerW,
    recipeCount,
    itemCount,
    backpackTopY,
}: {
    // First y the recipe list may draw at (below the tabs).
    topY: number
    // Last y the panel may draw at.
    bottomY: number
    // Panel width inside its margins.
    innerW: number
    recipeCount: number
    itemCount: number
    // When supplied, the backpack occupies a separate column starting at this y.
    backpackTopY?: number
}): CraftingLayout => {
    const available = Math.max(0, bottomY - topY)

    // Reserve what the backpack wants at the roomiest slot size given unlimited height, capped so a
    // full pack can't starve the recipe list -- past the cap the grid compensates with smaller slots.
    const preferred = gridFor(BACKPACK_SLOT_SIZES[0], itemCount, innerW, Number.MAX_SAFE_INTEGER)
    const cappedRows = Math.min(preferred.rows, MAX_RESERVED_ROWS)
    const reserved = backpackTopY === undefined ? SECTION_GAP + LABEL_H + rowsHeight(preferred.slot, cappedRows) : 0

    const viewH = Math.max(0, available - reserved)
    const cardH =
        recipeCount > 0
            ? Math.max(
                  RECIPE_CARD_MIN_H,
                  Math.min(RECIPE_CARD_MAX_H, Math.floor((viewH + RECIPE_GAP) / recipeCount) - RECIPE_GAP),
              )
            : 0

    const contentH = recipeCount > 0 ? recipeCount * (cardH + RECIPE_GAP) - RECIPE_GAP : RECIPE_EMPTY_H
    const windowH = Math.min(contentH, viewH)
    const maxScroll = Math.max(0, contentH - windowH)

    const recipesBottom = topY + windowH
    const labelY = backpackTopY ?? recipesBottom + SECTION_GAP
    const gridY = labelY + LABEL_H
    const grid = backpackGrid(itemCount, innerW, bottomY - gridY)

    return {
        cardH,
        viewH: windowH,
        contentH,
        maxScroll,
        recipesBottom,
        labelY,
        gridY,
        grid,
        bottom: Math.max(recipesBottom, gridY + grid.height),
    }
}

/** Keep a scroll offset inside its list, e.g. after the group changed or the pack grew. */
export const clampScroll = (scrollY: number, maxScroll: number): number =>
    Math.max(0, Math.min(scrollY, Math.max(0, maxScroll)))

/**
 * Where a recipe row lands ON THE PANEL, and whether any of it is in the scroll window.
 *
 * The recipe cards are drawn into a strip that is itself offset by the scroll, so a card's own
 * coordinates are not the panel's. That matters for one thing in particular: the ember tutorial's
 * third clue points at the campfire row's Craft button, and a clue anchor is registered in the clue
 * layer's space, which shares the PANEL's origin. Handing it strip coordinates put the pointer near
 * the panel's left edge, a scroll's distance from the button it was naming.
 *
 * `visible` is the other half. A clue aimed at a row scrolled out of the window is the same failure as
 * a clue with no anchor at all -- it points at something the player cannot see.
 */
export interface RecipeRowPlacement {
    x: number
    y: number
    visible: boolean
}

export const recipeRowPlacement = (
    index: number,
    cardH: number,
    scrollY: number,
    viewH: number,
    listX: number,
    listY: number,
): RecipeRowPlacement => {
    const top = index * (cardH + RECIPE_GAP)
    return {
        x: listX,
        y: listY + top - scrollY,
        // Partly visible counts: a row half off the bottom edge is still a row the player can see and
        // reach.
        visible: top + cardH > scrollY && top < scrollY + viewH,
    }
}
